import React, { useState } from 'react';
import { ActionIcon, CloseButton, Flex, Group, Text, TextInput } from '@mantine/core';
import { IconArrowLeft, IconSearch } from '@tabler/icons-react';
import { useContainerQuery } from 'src/UI/app-container/ContainerBreakpoint';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { CountBadge } from 'src/UI/page-nav/sidebar/CountBadge';
import { ClickableSectionTitle } from 'src/UI/page-nav/ClickableSectionTitle';
import classes from './PageHeader.module.css';

interface PageHeaderProps {
  title: string;
  badgeCount?: number;
  showSearch?: boolean;
  collapsibleSearch?: boolean;
  searchOpen?: boolean;
  onSearchOpenChange?: (open: boolean) => void;
  onBack?: () => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchResultCount?: number;
  searchPlaceholder?: string;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  variant?: 'default' | 'compact';
}

export function PageHeader({
  title,
  badgeCount,
  showSearch = false,
  collapsibleSearch = false,
  searchOpen: controlledSearchOpen,
  onSearchOpenChange,
  onBack,
  searchValue = '',
  onSearchChange,
  searchResultCount,
  searchPlaceholder = 'Search',
  actions,
  variant = 'default',
}: PageHeaderProps) {
  const cq = useContainerQuery();
  const [internalSearchOpen, setInternalSearchOpen] = useState(false);
  const searchOpen = controlledSearchOpen ?? internalSearchOpen;
  const isSearching = (showSearch || collapsibleSearch) && searchValue.trim().length > 0;
  const clearSearch = () => onSearchChange?.('');
  const isStacked = cq({ base: true, sm: false });
  const resultLabel = cq({ base: 'search results', sm: 'results', md: 'search results' });
  const isCompact = variant === 'compact';

  const titleSizeProps = isCompact
    ? { fz: 'xl' as const, lh: 1.5, fw: FONT_WEIGHTS.semibold }
    : {
        fz: cq({ base: 'displaySm', xl: 'displayMd' }),
        lh: cq({ base: 'displaySm', xl: 'displayMd' }),
        fw: FONT_WEIGHTS.medium,
      };

  const setSearchOpen = (open: boolean) => {
    if (onSearchOpenChange) {
      onSearchOpenChange(open);
      return;
    }
    setInternalSearchOpen(open);
  };

  const closeSearch = () => {
    clearSearch();
    setSearchOpen(false);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') closeSearch();
  };

  const handleSearchBlur = () => {
    if (searchValue.trim().length === 0) setSearchOpen(false);
  };

  const renderLeadingIcon = () => {
    if (isCompact) {
      if (!onBack) return null;
      return (
        <ActionIcon
          variant="transparent"
          bg="brand.0"
          c="brand.7"
          size={36}
          style={{
            // Plain borderRadius because the theme's actionIconRoot class overrides the radius prop
            borderRadius: 12,
          }}
          onClick={onBack}
          aria-label="Back"
        >
          <IconArrowLeft size={20} />
        </ActionIcon>
      );
    }
    if (isSearching) {
      return (
        <ActionIcon
          variant="subtle"
          color="gray.8"
          onClick={clearSearch}
          size="lg"
          aria-label="Clear search"
        >
          <IconArrowLeft size={22} />
        </ActionIcon>
      );
    }
    if (onBack) {
      return (
        <ActionIcon variant="subtle" color="gray.8" onClick={onBack} size="lg" aria-label="Back">
          <IconArrowLeft size={22} />
        </ActionIcon>
      );
    }
    return null;
  };

  const renderCollapsibleSearch = () => {
    if (searchOpen) {
      return (
        <TextInput
          value={searchValue}
          onChange={e => onSearchChange?.(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          onBlur={handleSearchBlur}
          placeholder={searchPlaceholder}
          leftSection={<IconSearch size={16} />}
          autoFocus
          w={218}
          style={{ flexShrink: 0 }}
          classNames={{ input: classes.searchInput, section: classes.searchSection }}
        />
      );
    }
    return (
      <ActionIcon
        variant="transparent"
        bg="white"
        c="gray.5"
        size={36}
        style={{
          flexShrink: 0,
          // Plain borderRadius because the theme's actionIconRoot class overrides the radius prop
          borderRadius: 12,
          border: '1px solid var(--mantine-color-gray-1)',
          boxShadow: '0 1px 2px rgba(10, 13, 18, 0.05)',
        }}
        aria-label="Open search"
        onClick={() => setSearchOpen(true)}
      >
        <IconSearch size={16} />
      </ActionIcon>
    );
  };

  return (
    <Flex
      direction={isStacked ? 'column' : 'row'}
      gap="sm"
      align={isStacked ? 'stretch' : 'center'}
      justify="space-between"
    >
      <Group gap="sm" wrap="nowrap">
        {renderLeadingIcon()}
        {isSearching ? (
          <Text {...titleSizeProps} c="gray.10" style={{ whiteSpace: 'nowrap' }}>
            {`${searchResultCount ?? 0} ${(searchResultCount ?? 0) === 1 ? 'result' : resultLabel}`}
          </Text>
        ) : (
          <ClickableSectionTitle
            {...titleSizeProps}
            c="gray.10"
            truncate
            style={{ flex: 1, minWidth: 0 }}
          >
            {title}
          </ClickableSectionTitle>
        )}
        {!isSearching && badgeCount !== undefined && badgeCount > 0 && (
          <CountBadge count={badgeCount} size="large" />
        )}
      </Group>

      <Group
        gap={isCompact ? 'xxs' : 'sm'}
        miw={0}
        wrap="nowrap"
        style={{
          flex: isStacked ? 1 : undefined,
          marginLeft: isStacked ? undefined : 'auto',
        }}
      >
        {collapsibleSearch && renderCollapsibleSearch()}
        {!collapsibleSearch && showSearch && (
          <TextInput
            placeholder={searchPlaceholder}
            leftSection={<IconSearch size={16} />}
            rightSection={searchValue ? <CloseButton size="sm" onClick={clearSearch} /> : undefined}
            value={searchValue}
            onChange={e => onSearchChange?.(e.target.value)}
            variant="compact"
            styles={{ input: { height: 40 } }}
            style={{ flex: isStacked ? 1 : undefined, minWidth: 0 }}
            w={isStacked ? undefined : cq({ sm: 260, xl: 320 })}
          />
        )}
        {actions}
      </Group>
    </Flex>
  );
}
